import React, { useState, useEffect } from 'react';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE, getImageUrl } from '../../utils/api';
import defaultImg from '../../assets/img/gl-1.png';
import { Link } from 'react-router-dom';

interface ClassifiedLead {
  _id: string;
  propertyCategory?: string;
  subCategory?: string;
  propertyType: string;
  price: number;
  area: string;
  city: string;
  description: string;
  buildingName?: string;
  bhkConfiguration?: string;
  size?: number;
  furnishingType?: string;
  possessionDate?: string;
  amenities?: string[];
  createdAt: string;
  owner?: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    role?: string;
  };
  images?: string[];
  contactName?: string;
  contactNumber?: string;
  contactEmail?: string;
  propertyAge?: string;
  floor?: string;
  totalFloors?: string;
  noOfParking?: string;
  washrooms?: number;
  totalUnit?: string;
  facing?: string;
  occupancyType?: string;
  actionType?: string;
  postedBy?: string;
  status?: string;
}

const capitalizeWords = (str: string) => {
  if (!str) return '';
  return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

const getInitials = (name: string) => {
  if (!name) return 'P';
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
};

export default function SavedClassifiedLeads() {
  const { token } = useAuth();
  const { theme } = useTheme();
  const [classifiedLeads, setClassifiedLeads] = useState<ClassifiedLead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');
  const [subCategoryFilter, setSubCategoryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [viewingDetailLead, setViewingDetailLead] = useState<ClassifiedLead | null>(null);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [savedLeads, setSavedLeads] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchSavedLeads = async () => {
    try {
      setLoadingLeads(true);
      const res = await fetch(`${API_BASE}/users/wishlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const items = data.classifiedWishlist || [];
        // Items are populated ClassifiedLead objects
        setClassifiedLeads(items);
        setSavedLeads(items.map((item: any) => item._id));
      }
    } catch (err) {
      console.error('Failed to fetch saved leads', err);
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    if (token) {
        fetchSavedLeads();
    }

    const params = new URLSearchParams(window.location.search);
    const searchQuery = params.get('search');
    if (searchQuery) {
      setLeadSearch(searchQuery);
    }
  }, [token]);

  const typeOptions = Array.from(new Set(classifiedLeads.map(l => l.propertyCategory || 'RESIDENTIAL'))).filter(Boolean);
  const subCategoryOptions = Array.from(new Set(classifiedLeads.map(l => l.subCategory || l.propertyType))).filter(Boolean);

  const filteredLeads = classifiedLeads.filter(lead => {
    const s = leadSearch.toLowerCase();
    const matchesSearch = !s || 
      lead.buildingName?.toLowerCase().includes(s) ||
      lead.propertyType?.toLowerCase().includes(s) ||
      lead.area?.toLowerCase().includes(s) ||
      lead.city?.toLowerCase().includes(s);

    const matchesType = propertyTypeFilter === 'all' || (lead.propertyCategory || 'RESIDENTIAL') === propertyTypeFilter;
    const matchesSubCategory = subCategoryFilter === 'all' || (lead.subCategory || lead.propertyType) === subCategoryFilter;

    // Date Filtering Logic
    const leadDate = new Date(lead.createdAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    let matchesDate = true;
    if (dateFilter === 'today') {
      matchesDate = leadDate >= today;
    } else if (dateFilter === 'yesterday') {
      matchesDate = leadDate >= yesterday && leadDate < today;
    } else if (dateFilter === 'last7days') {
      matchesDate = leadDate >= sevenDaysAgo;
    } else if (dateFilter === 'last30days') {
      matchesDate = leadDate >= thirtyDaysAgo;
    }

    const matchesStatus = statusFilter === 'all' || (lead.status || 'Active') === statusFilter;

    return matchesSearch && matchesType && matchesSubCategory && matchesDate && matchesStatus;
  });

  const toggleSaveLead = async (leadId: string) => {
    try {
      const res = await fetch(`${API_BASE}/users/wishlist/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ itemId: leadId, itemType: 'classified' })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.isSaved) {
          setSavedLeads(prev => [...prev, leadId]);
        } else {
          setSavedLeads(prev => prev.filter(id => id !== leadId));
          // If we unsave from the saved leads page, optionally remove it from the list immediately
          setClassifiedLeads(prev => prev.filter(lead => lead._id !== leadId));
        }
      }
    } catch (err) {
      console.error('Failed to toggle saved lead', err);
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      const res = await fetch(`${API_BASE}/classified-properties/${leadId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setClassifiedLeads(prev => prev.map(lead => 
          lead._id === leadId ? { ...lead, status: newStatus } : lead
        ));
      }
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const handleShareProperty = (lead: ClassifiedLead) => {
    const shareText = `New Property Available!\nType: ${lead.propertyType}\nLocation: ${lead.area}, ${lead.city}\nPrice: ${lead.price > 0 ? `₹${lead.price.toLocaleString()}` : 'Price on Request'}\nCheck it out on My Property Guru!`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (viewingDetailLead) {
    const images = viewingDetailLead.images && viewingDetailLead.images.length > 0 ? viewingDetailLead.images : [defaultImg];
    
    const nextImage = () => setCurrentImageIdx((prev) => (prev + 1) % images.length);
    const prevImage = () => setCurrentImageIdx((prev) => (prev - 1 + images.length) % images.length);

    return (
      <UserDashboardLayout>
        <div className="p-2 md:p-8 min-h-screen bg-[#f8f9fa]">
            <div className="flex justify-between items-center mb-6">
                <button onClick={() => setViewingDetailLead(null)} className="flex items-center gap-2 text-sm font-bold text-[#05144b] bg-white px-5 py-2.5 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100">
                    <i className="fa-solid fa-arrow-left"></i> Back to Ads
                </button>
                <button onClick={() => toggleSaveLead(viewingDetailLead._id)} className={`flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-xl shadow-sm hover:shadow-md transition-all border ${savedLeads.includes(viewingDetailLead._id) ? 'bg-[var(--purple)] text-white border-[var(--purple)]' : 'bg-white text-[#05144b] border-gray-100'}`}>
                    <i className={`fa-bookmark ${savedLeads.includes(viewingDetailLead._id) ? 'fa-solid' : 'fa-regular'} text-[var(--purple)]`}></i> {savedLeads.includes(viewingDetailLead._id) ? 'Saved' : 'Save Lead'}
                </button>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Hero Image Slider */}
                <div className="relative w-full h-[50vh] md:h-[60vh] bg-black group">
                    <img 
                        src={getImageUrl(images[currentImageIdx])} 
                        alt="Property" 
                        className="w-full h-full object-cover opacity-90 transition-opacity duration-500"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    
                    {/* Floating Info on Image */}
                    <div className="absolute bottom-4 md:bottom-10 left-4 md:left-10 right-4 md:right-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4 md:gap-6 z-10">
                        <div>
                            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                                <span className="bg-[#f1ae24] text-black text-[10px] md:text-xs font-black px-2 md:px-3 py-1 md:py-1.5 rounded-md uppercase tracking-widest shadow-lg">
                                    {viewingDetailLead.propertyCategory || viewingDetailLead.propertyType || 'Classified'}
                                </span>
                                <span className="bg-white/20 backdrop-blur-md text-white border border-white/30 text-[10px] md:text-xs font-bold px-2 md:px-3 py-1 md:py-1.5 rounded-md uppercase tracking-widest">
                                    {viewingDetailLead.subCategory || viewingDetailLead.propertyType}
                                </span>
                            </div>
                            <h2 className="text-2xl md:text-5xl font-black text-white mb-1 md:mb-2 leading-tight drop-shadow-md">
                                {viewingDetailLead.buildingName || viewingDetailLead.propertyType}
                            </h2>
                            <p className="text-white/90 font-medium text-xs md:text-base flex items-center gap-1.5 md:gap-2">
                                <i className="fa-solid fa-location-dot text-[#f1ae24]"></i>
                                {viewingDetailLead.area}, {viewingDetailLead.city}
                            </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md border border-white/20 p-3 md:p-4 rounded-xl md:rounded-2xl shadow-2xl shrink-0 self-start sm:self-auto">
                            <p className="text-white/80 text-[9px] md:text-xs font-bold uppercase tracking-widest mb-0.5 md:mb-1">
                                {viewingDetailLead.price > 0 ? 'Price' : 'Price On Request'}
                            </p>
                            {viewingDetailLead.price > 0 && (
                                <p className="text-xl md:text-3xl font-black text-[#f1ae24]">₹{viewingDetailLead.price.toLocaleString()}</p>
                            )}
                        </div>
                    </div>

                    {/* Slider Controls */}
                    {images.length > 1 && (
                        <>
                            <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/30 backdrop-blur-md border border-white/20 text-white rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-xl">
                                <i className="fa-solid fa-chevron-left"></i>
                            </button>
                            <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/30 backdrop-blur-md border border-white/20 text-white rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-xl">
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
                                {images.map((_, idx) => (
                                    <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentImageIdx ? 'w-8 bg-[#f1ae24]' : 'w-2 bg-white/50 cursor-pointer'}`} onClick={() => setCurrentImageIdx(idx)}></div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Content Section */}
                <div className="p-4 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
                    
                    {/* Left Column - Details */}
                    <div className="lg:col-span-2 space-y-10">
                        
                        {/* Key Features Grid */}
                        <div>
                            <h3 className="text-xl font-black text-[#05144b] mb-6 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center"><i className="fa-solid fa-list-check"></i></div>
                                Property Specifications
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-8 gap-x-6">
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-bed text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Bhk config</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.bhkConfiguration || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-ruler-combined text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Size / area</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.size ? `${viewingDetailLead.size} Sq.Ft` : 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-layer-group text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Floor details</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.floor || 'N/A'} {viewingDetailLead.totalFloors ? `/ ${viewingDetailLead.totalFloors}` : ''}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-couch text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Furnishing</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.furnishingType || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-calendar-check text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Property age</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.propertyAge || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-users text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Occupancy</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.occupancyType || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-toilet text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Washrooms</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.washrooms || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-building text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Total units</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.totalUnit || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-compass text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Facing</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.facing || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                                        <i className="fa-solid fa-square-parking text-gray-700 text-xl"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-black mb-0.5">Parking</p>
                                        <p className="text-base font-black text-black truncate">{viewingDetailLead.noOfParking || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <h3 className="text-xl font-black text-[#05144b] mb-6 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center"><i className="fa-solid fa-align-left"></i></div>
                                Description
                            </h3>
                            <div className="bg-gray-50 border border-gray-100 p-6 rounded-xl">
                                <p className="text-black font-semibold leading-relaxed text-lg md:text-xl whitespace-pre-wrap">{viewingDetailLead.description || 'No description provided.'}</p>
                            </div>
                        </div>

                        {/* Amenities (if any) */}
                        {viewingDetailLead.amenities && viewingDetailLead.amenities.length > 0 && (
                            <div>
                                <h3 className="text-xl font-black text-[#05144b] mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><i className="fa-solid fa-tree"></i></div>
                                    Amenities
                                </h3>
                                <div className="flex flex-wrap gap-3">
                                    {viewingDetailLead.amenities.map((amenity, idx) => (
                                        <div key={idx} className="bg-white border border-emerald-100 text-emerald-700 px-4 py-2 rounded-xl text-xs font-bold shadow-sm flex items-center gap-2">
                                            <i className="fa-solid fa-check text-emerald-500"></i> {amenity}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                    </div>

                    {/* Right Column - Contact Card */}
                    <div>
                        <div className="sticky top-24 bg-white rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100/80 relative overflow-hidden">
                            {/* Decorative background circle */}
                            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[var(--purple)]/5 rounded-full blur-3xl pointer-events-none"></div>
                            
                            {/* Avatar Header */}
                            <div className="flex flex-col items-center text-center pb-6 border-b border-gray-100/85 relative z-10">
                                <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-[var(--purple)] to-indigo-600 text-white flex items-center justify-center text-2xl font-black shadow-md border-4 border-white ring-4 ring-purple-100/50 shrink-0">
                                    {getInitials(viewingDetailLead.contactName || viewingDetailLead.owner?.name || 'Private Contact')}
                                </div>
                                <h4 className="text-xl font-black text-black mt-4 tracking-tight leading-tight">
                                    {capitalizeWords(viewingDetailLead.contactName || viewingDetailLead.owner?.name || 'Private Contact')}
                                </h4>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-600 mt-2">
                                    {capitalizeWords(viewingDetailLead.postedBy || viewingDetailLead.owner?.role || 'User')}
                                </span>
                            </div>

                            {/* Contact Details List */}
                            <div className="py-6 space-y-4 relative z-10">
                                <div className="flex items-center justify-between p-3.5 rounded-2xl bg-gray-50/70 hover:bg-gray-50 border border-gray-100/30 transition-all group">
                                    <div className="flex items-center gap-3.5 min-w-0">
                                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-500 border border-gray-100 shrink-0 group-hover:scale-105 transition-transform">
                                            <i className="fa-solid fa-phone text-base text-[var(--purple)]"></i>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Phone Number</p>
                                            <p className="text-[15px] font-black text-black truncate">
                                                {viewingDetailLead.contactNumber || viewingDetailLead.owner?.phone || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    {(viewingDetailLead.contactNumber || viewingDetailLead.owner?.phone) && (
                                        <a href={`tel:${viewingDetailLead.contactNumber || viewingDetailLead.owner?.phone}`} className="w-9 h-9 rounded-xl bg-white border border-gray-100 text-gray-500 hover:text-[var(--purple)] hover:border-[var(--purple)] flex items-center justify-center shadow-sm hover:shadow transition-all shrink-0">
                                            <i className="fa-solid fa-phone text-xs"></i>
                                        </a>
                                    )}
                                </div>

                                <div className="flex items-center justify-between p-3.5 rounded-2xl bg-gray-50/70 hover:bg-gray-50 border border-gray-100/30 transition-all group">
                                    <div className="flex items-center gap-3.5 min-w-0">
                                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-500 border border-gray-100 shrink-0 group-hover:scale-105 transition-transform">
                                            <i className="fa-solid fa-envelope text-base text-[var(--purple)]"></i>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Email Address</p>
                                            <p className="text-sm font-semibold text-black truncate">
                                                {viewingDetailLead.contactEmail || viewingDetailLead.owner?.email || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    {(viewingDetailLead.contactEmail || viewingDetailLead.owner?.email) && (
                                        <a href={`mailto:${viewingDetailLead.contactEmail || viewingDetailLead.owner?.email}`} className="w-9 h-9 rounded-xl bg-white border border-gray-100 text-gray-500 hover:text-[var(--purple)] hover:border-[var(--purple)] flex items-center justify-center shadow-sm hover:shadow transition-all shrink-0">
                                            <i className="fa-solid fa-envelope-open text-xs"></i>
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Full-width CTAs */}
                            <div className="flex flex-col gap-3 relative z-10">
                                {(viewingDetailLead.contactNumber || viewingDetailLead.owner?.phone) && (
                                    <a href={`tel:${viewingDetailLead.contactNumber || viewingDetailLead.owner?.phone}`} className="w-full bg-[#05144b] hover:bg-[#030c2d] text-white font-extrabold py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm uppercase tracking-wider">
                                        <i className="fa-solid fa-phone"></i> Call Contact
                                    </a>
                                )}
                                {(viewingDetailLead.contactNumber || viewingDetailLead.owner?.phone) && (
                                    <a href={`https://wa.me/${(viewingDetailLead.contactNumber || viewingDetailLead.owner?.phone)?.replace(/\D/g, '')}?text=Hi, I am interested in your property ${viewingDetailLead.buildingName || viewingDetailLead.propertyType} listed on My Property Guru.`} target="_blank" rel="noopener noreferrer" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold py-3.5 rounded-xl shadow-md hover:shadow-lg hover:shadow-emerald-100 transition-all flex items-center justify-center gap-2 text-sm uppercase tracking-wider">
                                        <i className="fa-brands fa-whatsapp text-lg"></i> WhatsApp
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="px-0 py-6 md:px-8 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-10">

          <div className="glass-card p-2 md:p-8 border-[var(--purple)]/10 space-y-6 md:space-y-8 animate__animated animate__fadeIn">
            <div className="flex flex-col lg:flex-row gap-4 md:gap-6 items-center">
              <div className="flex flex-col md:flex-row gap-3 md:gap-4 w-full flex-1">
                <div className="relative flex-1 group">
                  <i className="fa-solid fa-magnifying-glass absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-[var(--purple)] text-xs md:text-sm"></i>
                  <input
                    type="text"
                    placeholder="Search asset, area..."
                    className="w-full pl-10 md:pl-14 pr-4 md:pr-6 py-3 md:py-4 bg-[var(--bg-main)] border border-[var(--purple)]/20 rounded-xl md:rounded-2xl text-xs md:text-base font-bold focus:outline-none focus:border-[var(--purple)] transition-all placeholder:text-[var(--text-secondary)] text-[var(--text-primary)] shadow-inner"
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4 w-full md:w-auto">
                  <select
                    className="flex-1 px-3 md:px-6 py-3 md:py-4 bg-[var(--bg-main)] border border-[var(--purple)]/20 rounded-xl md:rounded-2xl text-[10px] md:text-xs font-bold focus:outline-none focus:border-[var(--purple)] appearance-none text-[var(--text-primary)] shadow-inner"
                    value={propertyTypeFilter}
                    onChange={(e) => setPropertyTypeFilter(e.target.value)}
                  >
                    <option value="all">Type: All</option>
                    {typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>

                  <select
                    className="flex-1 px-3 md:px-6 py-3 md:py-4 bg-[var(--bg-main)] border border-[var(--purple)]/20 rounded-xl md:rounded-2xl text-[10px] md:text-xs font-bold focus:outline-none focus:border-[var(--purple)] appearance-none text-[var(--text-primary)] shadow-inner"
                    value={subCategoryFilter}
                    onChange={(e) => setSubCategoryFilter(e.target.value)}
                  >
                    <option value="all">Category: All</option>
                    {subCategoryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>

                  <select
                    className="flex-1 px-3 md:px-6 py-3 md:py-4 bg-[var(--bg-main)] border border-[var(--purple)]/20 rounded-xl md:rounded-2xl text-[10px] md:text-xs font-bold focus:outline-none focus:border-[var(--purple)] appearance-none text-[var(--text-primary)] shadow-inner"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                  >
                    <option value="all">Date: All Time</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7days">Last 7 Days</option>
                    <option value="last30days">Last 30 Days</option>
                  </select>

                  <select
                    className="flex-1 px-3 md:px-6 py-3 md:py-4 bg-[var(--bg-main)] border border-[var(--purple)]/20 rounded-xl md:rounded-2xl text-[10px] md:text-xs font-bold focus:outline-none focus:border-[var(--purple)] appearance-none text-[var(--text-primary)] shadow-inner"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Status: All</option>
                    <option value="Active">Active</option>
                    <option value="Sold Out">Sold Out</option>
                    <option value="Expired">Expired</option>
                  </select>
                </div>
              </div>
            </div>

            {loadingLeads ? (
              <div className="text-center py-24">
                <div className="w-16 h-16 border-4 border-[var(--purple)] border-t-transparent rounded-full animate-spin mx-auto mb-6 shadow-xl"></div>
                <p className="font-bold text-[var(--text-secondary)]">Syncing Asset Stream...</p>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-24 bg-[var(--purple)]/5 rounded-[3rem] border-2 border-dashed border-[var(--purple)]/10">
                <i className="fa-solid fa-heart-crack text-6xl text-[var(--purple)]/20 mb-6"></i>
                <h4 className="text-2xl font-bold text-[var(--text-primary)]">No Saved Leads Found</h4>
                <p className="font-bold text-[var(--text-secondary)] text-sm">Save some leads from the classified ads to see them here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredLeads.map((lead, idx) => (
                  <div key={lead._id} className="flex flex-col h-full bg-white rounded-xl md:rounded-2xl border border-gray-100/50 shadow-sm hover:shadow-[0_20px_50px_rgba(0,102,255,0.25)] hover:-translate-y-2 transition-all duration-300 group animate__animated animate__fadeInUp" style={{ animationDelay: `${idx * 0.05}s` }}>
                      {/* Top Image Section */}
                      <div className="relative h-[200px] md:h-[230px] w-full rounded-t-xl md:rounded-t-2xl overflow-hidden shadow-inner">
                          <div className="block w-full h-full relative z-0 cursor-pointer" onClick={() => { setViewingDetailLead(lead); setCurrentImageIdx(0); }}>
                              <img
                                  src={lead.images && lead.images.length > 0 ? getImageUrl(lead.images[0]) : defaultImg}
                                  alt={lead.buildingName || lead.propertyType}
                                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                  loading="lazy"
                              />
                              <div className="absolute inset-x-0 bottom-0 h-16 md:h-20 bg-gradient-to-t from-[#1e2329]/90 to-transparent"></div>
                          </div>

                          {/* Badges */}
                          <div className="absolute top-3 left-3 bg-[#f1ae24] text-black px-2.5 py-1 rounded-full font-black text-[9px] md:text-[10px] tracking-widest uppercase flex items-center gap-1 shadow-md pointer-events-none">
                              {lead.propertyCategory || lead.propertyType || 'CLASSIFIED'}
                          </div>
                          
                          <div className="absolute bottom-2 md:bottom-3 left-3 md:left-4 bg-[#05144b] text-white px-2.5 py-1 rounded-full font-black text-[9px] md:text-[10px] tracking-widest uppercase flex items-center gap-1.5 shadow-[0_4px_10px_rgba(5,20,75,0.4)] border border-[#1e3486] pointer-events-none z-10 transition-transform group-hover:-translate-y-1">
                              <i className="fa-solid fa-tag text-[#f1ae24]"></i> {lead.subCategory || lead.propertyType}
                          </div>

                          {/* Right Floating Actions */}
                          <div className="absolute bottom-2 md:bottom-3 right-3 md:right-4 flex gap-1.5 md:gap-2 z-20">
                              <button onClick={() => handleShareProperty(lead)} className="w-[30px] h-[30px] md:w-[34px] md:h-[34px] rounded-full bg-[#2a2a2a] text-white flex items-center justify-center hover:bg-[#f1ae24] hover:text-black hover:scale-105 active:scale-95 transition-all shadow-[0_4px_10px_rgba(0,0,0,0.3)] border border-[#444]" title="Share">
                                  <i className="fa-solid fa-share-nodes text-[12px] md:text-[14px]"></i>
                              </button>
                               <button onClick={() => toggleSaveLead(lead._id)} className={`w-[30px] h-[30px] md:w-[34px] md:h-[34px] rounded-full flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-[0_4px_10px_rgba(0,0,0,0.3)] border ${savedLeads.includes(lead._id) ? 'bg-[var(--purple)] text-white border-[var(--purple)]' : 'bg-[#2a2a2a] text-white border-[#444] hover:bg-[#f1ae24] hover:text-black hover:border-transparent'}`} title={savedLeads.includes(lead._id) ? "Remove from Saved" : "Save Lead"}>
                                  <i className={`fa-bookmark ${savedLeads.includes(lead._id) ? 'fa-solid text-[#f1ae24]' : 'fa-regular'} text-[12px] md:text-[14px]`}></i>
                              </button>
                          </div>
                      </div>

                      {/* Card Details Section */}
                      <div className="p-3 md:p-4 flex flex-col flex-grow bg-white relative z-10 rounded-b-xl md:rounded-b-2xl">
                          <h4 className="text-[17px] md:text-[19px] font-black text-black uppercase tracking-wide leading-tight truncate cursor-pointer hover:text-black/70 transition-colors" onClick={() => { setViewingDetailLead(lead); setCurrentImageIdx(0); }}>
                              {lead.buildingName || lead.propertyType}
                          </h4>

                          <div className="text-black/80 text-[11px] md:text-[12px] mt-0.5 flex items-start gap-1 font-semibold truncate mb-1 lg:mb-1.5">
                              <i className="fa-solid fa-location-dot mt-[2px] text-black/60"></i>
                              <span className="truncate">{lead.area}, {lead.city}</span>
                          </div>

                          {/* Price */}
                          <div className="text-black font-black text-[17px] md:text-[19px] mb-0.5 tracking-tight drop-shadow-sm truncate">
                              {lead.price > 0 ? `₹${lead.price.toLocaleString()}` : 'Price on Request'}
                          </div>

                          {/* Config */}
                          <div className="text-black font-bold text-[11px] md:text-[12px] mb-2 md:mb-3 truncate">
                              {lead.bhkConfiguration ? (lead.bhkConfiguration.toLowerCase().includes('bhk') ? lead.bhkConfiguration : `${lead.bhkConfiguration} BHK`) : 'Configuration N/A'}
                          </div>

                           {/* Posted By */}
                           <div className="flex items-center gap-2 mb-3">
                               <span className="text-[9px] font-black text-[#05144b] bg-[#05144b]/5 px-2 py-0.5 rounded-md uppercase tracking-wider">
                                   Posted By: {lead.postedBy || lead.owner?.role || 'User'}
                               </span>
                           </div>

                          {/* Specifications Grids */}
                          <div className="grid grid-cols-2 gap-x-2 gap-y-2 text-black text-[10px] md:text-[11px] font-bold mt-auto border-t border-gray-100 pt-3 pb-3 overflow-hidden">
                              <div className="flex items-center gap-1.5 shrink-0">
                                  <i className="fa-solid fa-ruler-combined text-black/80 text-[12px]"></i>
                                  <span className="truncate">{lead.size ? `${lead.size} Sq.Ft` : 'Size N/A'}</span>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                  <i className="fa-solid fa-layer-group text-black/80 text-[12px]"></i>
                                  <span className="truncate">{lead.floor ? (lead.totalFloors ? `${lead.floor} / ${lead.totalFloors} Floor` : `${lead.floor} Floor`) : (lead.totalFloors ? `${lead.totalFloors} Floors` : 'Floor N/A')}</span>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                  <i className="fa-solid fa-couch text-black/80 text-[12px]"></i>
                                  <span className="truncate">{lead.furnishingType || 'Unfurnished'}</span>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                  <i className="fa-solid fa-clock text-black/80 text-[12px]"></i>
                                  <span className="truncate">{new Date(lead.createdAt).toLocaleDateString()}</span>
                              </div>
                          </div>

                          {/* Bottom Actions */}
                          <div className="flex flex-row items-stretch sm:items-center justify-between gap-2 border-t border-gray-100 pt-2.5 w-full mt-auto">
                              <button
                                  onClick={() => { setViewingDetailLead(lead); setCurrentImageIdx(0); }}
                                  className="w-full text-white text-[9px] md:text-[10.5px] font-black px-4 sm:px-5 py-2 md:py-2.5 rounded-[5px] uppercase tracking-widest whitespace-nowrap shadow-md hover:brightness-110 transition-all text-center"
                                  style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}
                              >
                                  VIEW DETAILS <i className="fa-solid fa-chevron-right ml-0.5 text-[8px] opacity-70"></i>
                              </button>
                          </div>
                      </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </UserDashboardLayout>
  );
}
